<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useUserStore, useCommonStore, useQuoteStore } from '@/shared/store'
  import { type Plan, FileCategory, WordingDocName } from '@/shared/types/api.types'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import InformationCard from '@/shared/components/InformationCard/InformationCard.vue'
  import PdfIcon from '@/shared/components/icons/PdfIcon.vue'
  import FlowPlan from '@/entities/Flow/components/FlowPlan/FlowPlan.vue'
  import { useI18n } from 'vue-i18n'
  import { formatDate } from '@qic/ui-components'
  // import TravelersPopup from '@/widgets/Flow/FlowSelectPlanWidget/components/TravelersPopup/TravelersPopup.vue';
  // import BaseDatePicker from '@/shared/components/BaseDatePicker/BaseDatePicker.vue';
  import {
    LOAD_EVENT_NAME,
    // MAX_DEPARTURE_DAY_WITHOUT_TODAY,
    MULTI_TRIP_RETURN_DAY,
    PROCEED_EVENT_NAME,
  } from './FlowSelectPlanInboundWidget.constants'
  // import { getUnixTimeStamp } from '@/shared/utils/UnixTime';
  import { InboundFlowRoutes } from '@/shared/types/router.types'
  import { useGoogleAnalytics, usePerformanceTiming } from '@/shared/composables'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'

  const emit = defineEmits<Emits>()

  const { push } = useRouter()

  const { getFormData } = useFormDataWithRedirect()

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void

    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  const { t, locale } = useI18n()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const quoteStore = useQuoteStore()
  const policyWording = ref('')
  // @ts-ignore
  const departureDate = ref<Date>(userStore.departureDate)
  // @ts-ignore
  const returnDate = ref<Date>(userStore.returnDate)
  // const popup = ref<'travelers' | 'datepicker' | null>(null);
  const adultsCount = computed(() => userStore.adultsCount)
  const childrenCount = computed(() => userStore.childrenCount)
  const travelersCount = adultsCount.value + childrenCount.value
  const plan = ref<Plan | null>(null)
  // const focusedDate = ref();
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const policyEndDate = computed(() => {
    if (!departureDate.value) return

    const dt = new Date(departureDate.value)
    const newDay = dt.getDate() + MULTI_TRIP_RETURN_DAY
    const dmDt = new Date(dt.setDate(newDay))
    const newDate = dmDt.setDate(dmDt.getDate())

    return formatDate(new Date(newDate))
  })
  const isSingle = computed(() => userStore.tripType === 'SINGLE')

  const informationCardOptions = computed(() => [
    {
      id: 'date',
      title: t('inbound.plan.policy_information.period'),
      // @ts-ignore
      content: `${formatDate(departureDate.value, locale.value)} - ${formatDate(
        // @ts-ignore
        isSingle.value ? returnDate.value : policyEndDate.value,
        locale.value
      )}`,
      isClickable: false,
      onOpen: () => {
        // @ts-ignore
        gaService.sendSelectPlanChangeDate(plan.value?.price, departureDate.value, returnDate.value)
      },
    },
    {
      id: 'travelers',
      title: t('inbound.plan.policy_information.travellers'),
      content: `${t(
        'outbound.plans.policy_information.people',
        { personCount: travelersCount },
        travelersCount
      )}`,
      isClickable: false,
      onOpen: () => {
        // @ts-ignore
        gaService.sendSelectPlanChangeTravelers(plan.value.price, travelersCount)
      },
    },
  ])

  // const singleTripDates = computed({
  //   get: () => {
  //     return {
  //       start: departureDate.value,
  //       end: returnDate.value
  //     };
  //   },
  //   set: (dates) => {
  //     departureDate.value = dates.start;
  //     returnDate.value = dates.end;
  //   }
  // });
  // const minDepartureDate = new Date();
  // const maxDate = computed(() => {
  //   const today = focusedDate.value?.start ? focusedDate.value.start : new Date();
  //   return new Date(today.setDate(today.getDate() + MAX_DEPARTURE_DAY_WITHOUT_TODAY));
  // });
  // const handleDayClick = (date) => {
  //   focusedDate.value = date;
  // };

  // const onDatesSave = async () => {
  //   const formattedDepartureDate = isSingle.value ? singleTripDates.value.start : departureDate.value;
  //   if (formattedDepartureDate === null) {
  //     onClose();
  //     return;
  //   }

  //   const formattedReturnDate: Date | null = isSingle.value
  //     ? singleTripDates.value.end
  //     : new Date(
  //         new Date(formattedDepartureDate).setDate(
  //           formattedDepartureDate.getDate() + MULTI_TRIP_RETURN_DAY
  //         )
  //       );

  //   if (!isSingle.value) {
  //     returnDate.value = formattedReturnDate;
  //   }

  //   if (isSingle.value && formattedReturnDate === null) {
  //     onClose();
  //     return;
  //   }
  //   if (
  //     formattedReturnDate?.getTime() === userStore.returnDate?.getTime() &&
  //     userStore.departureDate?.getTime() === formattedDepartureDate?.getTime()
  //   ) {
  //     onClose();
  //     return;
  //   }
  //   userStore.setTravelDates(formattedDepartureDate, formattedReturnDate, true);
  //   onClose();
  //   await emit(LOAD_EVENT_NAME, async () => {
  //     await commonStore.sendFormData({
  //       policyDate: getUnixTimeStamp(formattedDepartureDate),
  //       endDate:
  //         formattedReturnDate !== null ? getUnixTimeStamp(formattedReturnDate) : formattedReturnDate
  //     });
  //     await getQuote();
  //   });
  // };

  const onProceed = () => {
    if (plan.value) {
      userStore.setSelectedPlan(plan.value.schemecode, true)
    }
    gaService.sendSelectPlanProceed(
      // @ts-ignore
      plan.value.price,
      travelersCount,
      departureDate.value,
      returnDate.value
    )

    emit(PROCEED_EVENT_NAME)
  }

  const fetchQatarWordings = () =>
    commonStore
      .getQatarWordings(FileCategory.TRAVEL, WordingDocName.POLICY_WORDING_INBOUND)
      .then((data) => {
        policyWording.value = data[0]?.Link || ''
      })

  onMounted(async () => {
    // TODO: Think about: Should we make API requests only in vuew layer? And in widget just emit events for this?
    await emit(LOAD_EVENT_NAME, async () => {
      // @ts-ignore
      if (!Object.keys(userStore.residenceCountry).length) {
        await push({ name: InboundFlowRoutes.RESIDENCE })
        return
      }

      if (!(await getFormData(TravelDirection.INBOUND, false))) return
      await Promise.all([getQuote(), fetchQatarWordings()])

      onPerformanceCheckEnd()
    })
  })

  const onPolicyWordingClick = () => {
    // @ts-ignore
    gaService.sendSelectPlanPolicyWordings(plan.value.price)
  }

  const getQuote = async (): Promise<void> => {
    const quote = await quoteStore.getTravelQuoteUae(userStore.promocode ?? '', 0)
    if (quote.schemes.length === 0) {
      return Promise.reject('quote.schemes.length === 0')
    } else {
      plan.value = quote.schemes[0]
      return Promise.resolve()
    }
  }

  // const onTravelersSave = async (newValue: { adultsCount: number; childrenCount: number }) => {
  //   if (
  //     userStore.adultsCount !== newValue.adultsCount ||
  //     userStore.childrenCount !== newValue.childrenCount
  //   ) {
  //     const dataToSend = {
  //       numAdults: newValue.adultsCount,
  //       numChildren: newValue.childrenCount
  //     };
  //     userStore.setTravelersCount(newValue.adultsCount, newValue.childrenCount, true);
  //     await emit(LOAD_EVENT_NAME, async () => {
  //       await commonStore.sendFormData(dataToSend);
  //       await getQuote();
  //     });
  //   }
  // };

  // const onClose = () => {
  //   focusedDate.value = undefined;
  //   popup.value = null;
  // };

  // const onTravelersClose = (data: { adultsCount: number; childrenCount: number }) => {
  //   onTravelersSave(data);
  //   onClose();
  // };
  const sendCoverToggle = (planName: string, planPrice: number, coverName: string) => {
    gaService.sendCoverToggle({
      category: TravelDirection.INBOUND,
      numOfTravelers: travelersCount,
      section: 'inclusive_cover',
      planName,
      planPrice,
      coverName,
    })
  }
</script>

<template>
  <FlowCard
    v-if="plan"
    :title="t('inbound_travel_insurance_header')"
    class="flow-select-plan-inbound-view"
    @button-click="onProceed"
  >
    <div class="flow-select-plan-widget__plans">
      <InformationCard
        :title="t('inbound.plan.policy_information.header')"
        :options="informationCardOptions"
        variant="travel"
      >
        <template #icon>
          <PdfIcon />
        </template>
      </InformationCard>
      <FlowPlan
        data-test="select-plan-inbound"
        :title="plan.name"
        variant="single"
        :policy-wording="policyWording"
        :plan="plan"
        @policy-wording-click="onPolicyWordingClick"
        @toggle="
          (_, __, coverName) => {
            // @ts-ignore
            sendCoverToggle(plan.name, plan.price, coverName)
          }
        "
      >
        <template #policyWording>
          {{ $t('inbound.view_plan.policy_wording') }}
        </template>
      </FlowPlan>
    </div>
    <!--
    	<TravelersPopup
    	v-if="popup === 'travelers'"
    	:adults="adultsCount"
    	:children="childrenCount"
    	@save="onTravelersSave"
    	@close="onTravelersClose"
    	/> 
    -->
    <!--
    	<BaseDatePicker
    	v-if="popup === 'datepicker' && isSingle"
    	v-model="singleTripDates"
    	in-modal
    	is-range
    	:start-date-label="t('qic.travelDates.departure')"
    	:end-date-label="t('qic.travelDates.return')"
    	:min-date="minDepartureDate"
    	:max-date="maxDate"
    	@day-focus="handleDayClick"
    	:mobile-title="t('qic.travelDates.multitripTitle')"
    	@close="onDatesSave"
    	/> 
    -->
    <!--
    	<BaseDatePicker
    	v-if="popup === 'datepicker' && !isSingle"
    	v-model="
    	departureDate
    	"
    	in-modal
    	:start-date-label="t('qic.travelDates.departure')"
    	:end-date-label="t('qic.travelDates.return')"
    	:min-date="minDepartureDate"
    	:max-date="maxDate"
    	@day-focus="handleDayClick"
    	:mobile-title="t('qic.travelDates.calendarTitle')"
    	@close="onDatesSave"
    	>
    	<template #fixed-footer>
    	<div class="date-picker-footer">
    	{{ $t('qic.travelDates.inboundFooter.multitripFooter_1') }}
    	<strong>{{
    	formatDate(departureDate)
    	}}</strong>
    	{{ $t('qic.travelDates.inboundFooter.multitripFooter_2') }}
    	<strong>{{ policyEndDate }}.</strong> <br />
    	{{ $t('qic.travelDates.inboundFooter.multitripFooter_3') }}
    	</div>
    	</template>
    	<template #footer>
    	<div class="date-picker-footer">
    	{{ $t('qic.travelDates.inboundFooter.multitripFooter_1') }}
    	<strong>{{
    	formatDate(departureDate)
    	}}</strong>
    	{{ $t('qic.travelDates.inboundFooter.multitripFooter_2') }}
    	<strong>{{ policyEndDate }}.</strong> <br />
    	{{ $t('qic.travelDates.inboundFooter.multitripFooter_3') }}
    	</div>
    	</template>
    	</BaseDatePicker> 
    -->
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowSelectPlanInboundWidget';
</style>

<style lang="scss">
  .flow-select-plan-inbound-view {
    .btn.base-card__button {
      max-width: 100%;
    }
  }
</style>
